import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CredentialsStatus, PmsSettings } from "@/types/pms";
import { PmsType } from "@/types/hotel";
import {
  PMS_PROVIDER_LABEL_KEYS,
  resolveActivePmsProvider,
} from "@/utils/pmsProviderLabels";
import { Plug, PlugZap } from "lucide-react";

interface PmsConnectorCardProps {
  settings: PmsSettings | null;
  credentials: CredentialsStatus | null;
  canWrite: boolean;
  isEnabling: boolean;
  isDisabling: boolean;
  onEnable: () => void;
  onDisable: () => void;
  hotelProvider?: PmsType;
}

export const PmsConnectorCard = ({
  settings,
  credentials,
  canWrite,
  isEnabling,
  isDisabling,
  onEnable,
  onDisable,
  hotelProvider,
}: PmsConnectorCardProps) => {
  const { t } = useTranslation();

  const provider = resolveActivePmsProvider(
    settings?.provider,
    credentials?.provider,
    hotelProvider,
  );

  const hasCredentials = credentials?.hasCredentials ?? false;
  const isEnabled = settings?.enabled ?? false;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("pms.connectors.title")}</CardTitle>
        <CardDescription>{t("pms.connectors.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {!provider ? (
          <p className="text-muted-foreground text-sm">
            {t("pms.connectors.empty")}
          </p>
        ) : (
          <div className="border-base-border flex flex-col gap-3 rounded-lg border p-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">
                {t(PMS_PROVIDER_LABEL_KEYS[provider])}
              </span>
              {isEnabled ? (
                <Badge variant="filled_status_completed">
                  {t("pms.connectors.connected")}
                </Badge>
              ) : (
                <Badge variant="new_destructive">
                  {t("pms.connectors.disconnected")}
                </Badge>
              )}
              <Badge
                variant={
                  hasCredentials ? "filled_status_completed" : "new_outline"
                }
              >
                {hasCredentials
                  ? t("pms.status.configured")
                  : t("pms.connectors.credentialsMissing")}
              </Badge>
            </div>

            {isEnabled ? (
              <Button
                variant="new_outline"
                disabled={!canWrite || isDisabling}
                onClick={onDisable}
              >
                <Plug />
                {t("pms.toggle.disable")}
              </Button>
            ) : (
              <Button
                variant="new_default"
                disabled={!canWrite || !hasCredentials || isEnabling}
                onClick={onEnable}
              >
                <PlugZap />
                {t("pms.toggle.enable")}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
